import React, { useState, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  deleteField,
  type UpdateData
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { COLLECTIONS } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import TextAreaField from '../../components/ui/TextAreaField';
import PlusIcon from '../../components/icons/PlusIcon';
import ContentCopyIcon from '../../components/icons/ContentCopyIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import EditPencilIcon from '../../components/icons/EditPencilIcon';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AssetPackHowToGuideContent } from './AssetPackHowToGuide';
import MainLayout from '../../components/layout/MainLayout';
import LeftArrowIcon from '../../components/icons/LeftArrowIcon';
import {
  parseArcadeShareUrlStrict,
  parseCodeExampleShareUrl,
  extractArcadeRunIdFromSimulatorEmbed
} from '../../utils/makeCodeArcadeShareUrl';

const COLLECTION = COLLECTIONS.ARCADE_ASSET_PACKS;

/** 갤러리·음악 공유·코드 예제 — 동일 컬렉션, category 로 구분 */
export type ArcadePackListing = 'gallery' | 'codeExample' | 'music';

/** Firestore: codeExample 또는 예전 sampleAssignment(호환) */
function isCodeExampleCategory(cat?: string): boolean {
  return cat === 'codeExample' || cat === 'sampleAssignment';
}

function isMusicCategory(cat?: string): boolean {
  return cat === 'music';
}

const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** GameRegister 시뮬레이터 iframe HTML과 동일 */
const SIMULATOR_CODE_MAX_CHARS = 100_000;

/**
 * MakeCode 공식 embed는 보통 padding-bottom ~117% (가로 대비 세로)로 시뮬레이터 전체를 담음.
 * 고정 vh/낮은 %만 쓰면 아래가 잘림.
 */
const SIMULATOR_EMBED_ASPECT_PADDING = '122%';
const PACK_CARD_PREVIEW_ASPECT = '16 / 10';

const skeletonPulseStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #EEE 25%, #F5F5F5 50%, #EEE 75%)',
  backgroundSize: '200% 100%',
  animation: 'assetPackSkeletonShimmer 1.2s ease-in-out infinite',
};

function PackCardSkeleton() {
  return (
    <Card padding="16px" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        style={{
          width: '100%',
          aspectRatio: PACK_CARD_PREVIEW_ASPECT,
          borderRadius: '8px',
          ...skeletonPulseStyle,
        }}
      />
      <div style={{ height: 20, width: '70%', borderRadius: 6, ...skeletonPulseStyle }} />
      <div style={{ height: 14, width: '45%', borderRadius: 6, ...skeletonPulseStyle }} />
      <div style={{ height: 48, width: '100%', borderRadius: 6, ...skeletonPulseStyle }} />
      <div style={{ height: 36, width: '100%', borderRadius: 8, ...skeletonPulseStyle }} />
    </Card>
  );
}

function CodeExampleSimulatorCardPreview({ html }: { html: string }) {
  const runId = extractArcadeRunIdFromSimulatorEmbed(html);
  if (runId) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 0,
          paddingBottom: SIMULATOR_EMBED_ASPECT_PADDING,
          minWidth: 0,
          background: 'var(--Color-fill-normal, #293044)',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}
      >
        <iframe
          title="MakeCode Arcade 시뮬레이터"
          src={`https://arcade.makecode.com/---run?id=${encodeURIComponent(runId)}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          allowFullScreen
          sandbox="allow-popups allow-forms allow-scripts allow-same-origin"
        />
      </div>
    );
  }
  return (
    <div
      style={{
        width: '100%',
        minWidth: 0,
        overflow: 'auto',
        overflowX: 'hidden',
        borderRadius: 8,
        border: '1px solid var(--Color-line-normal, #F0F0F0)',
        background: 'var(--Color-background-main)',
        WebkitOverflowScrolling: 'touch'
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120) || 'file';
}

interface ArcadeAssetPack {
  id: string;
  title: string;
  description: string;
  shareUrl: string;
  authorId: string;
  authorName: string;
  createdAt: Timestamp;
  /** Storage 업로드 미리보기 */
  previewImageUrl?: string;
  /** zip·git bundle 등 첨부 */
  attachmentUrl?: string;
  attachmentFileName?: string;
  /** gallery: 에셋 팩. music: 음악 공유. codeExample·sampleAssignment(레거시): 코드 예제 */
  category?: ArcadePackListing | 'sampleAssignment';
  /** 코드 예제: MakeCode 시뮬레이터 iframe HTML (GameRegister의 simulatorCode와 동일) */
  simulatorCode?: string;
  /** 코드 예제 적용 시 유의사항 */
  cautionNote?: string;
  /** 예제 실행 전 추가해야 하는 확장(쉼표 구분) */
  requiredExtensions?: string;
}

export interface AssetPackShareProps {
  listing?: ArcadePackListing;
}

export const AssetPackShare: React.FC<AssetPackShareProps> = ({ listing = 'gallery' }) => {
  const navigate = useNavigate();
  const isCodeExample = listing === 'codeExample';
  const isMusic = listing === 'music';
  const isGallery = listing === 'gallery';
  const isGalleryLike = isGallery || isMusic;
  const usesMainLayout = isCodeExample || isMusic;
  const storagePrefix = isCodeExample
    ? 'makecodearcade/codeExamples'
    : isMusic
      ? 'makecodearcade/music'
      : 'makecodearcade/assetPacks';

  const labels =
    isCodeExample
      ? {
          pageTitle: '코드 예제 공유',
          pageLead:
            '아래 예제 코드를 활용해서 여러분들만의 게임에 적용해보세요.',
          registerCta: '코드 예제 등록',
          listLoadError: '코드 예제 목록을 불러오지 못했습니다.',
          empty: '아직 등록된 코드 예제가 없습니다.',
          dialogTitle: '코드 예제 등록',
          deleteTitle: '코드 예제 삭제',
          successRegister: '코드 예제가 등록되었습니다.',
          descPlaceholder: '어떤 코드·패턴을 보여 주는지 간단히 적어 주세요.',
          titlePlaceholder: '예: 점프와 중력 예제',
          shareUrlFieldLabel: '공유 링크',
          shareUrlPlaceholder: 'https://arcade.makecode.com/… 또는 https://makecode.com/_…',
          shareUrlHelp:
            '예: https://arcade.makecode.com/… 또는 https://makecode.com/_... 붙여 넣으면 됩니다.',
          shareUrlError:
            '공유 링크를 확인해 주세요. (arcade.makecode.com 또는 makecode.com/_ 로 시작하는 공유 주소)',
          simulatorFieldLabel: '시뮬레이터 코드',
          simulatorPlaceholder: '시뮬레이터 임베드 코드',
          simulatorHelp: 'MakeCode Arcade에서 제공하는 시뮬레이터 임베드 코드를 입력하세요',
          cautionLabel: '유의사항',
          cautionPlaceholder: '예: 스프라이트 이름을 player로 맞춰 주세요.',
          extensionLabel: '필요한 확장',
          extensionPlaceholder: '예: arcade-text, story, tilemaps'
        }
      : isMusic
        ? {
            pageTitle: '음악 공유',
            pageLead:
              'MakeCode Arcade에서 음악·효과음 프로젝트를 공유하면 받은 주소를 올리고, 스크린샷·소스(zip 등) 첨부를 함께 넣을 수 있습니다.',
            registerCta: '음악 프로젝트 등록',
            listLoadError: '음악 목록을 불러오지 못했습니다.',
            empty: '아직 등록된 음악이 없습니다.',
            dialogTitle: '음악 프로젝트 등록',
            deleteTitle: '음악 프로젝트 삭제',
            successRegister: '음악 프로젝트가 등록되었습니다.',
            descPlaceholder: '어떤 멜로디·효과음인지 간단히 적어 주세요.',
            titlePlaceholder: '예: 보스전 BGM',
            shareUrlFieldLabel: 'MakeCode 공유 주소',
            shareUrlPlaceholder: 'https://arcade.makecode.com/S65139-04309-19865-34038',
            shareUrlHelp: 'Arcade 편집기에서 「공유」로 받은 링크만 붙여 넣을 수 있습니다.',
            shareUrlError:
              'MakeCode Arcade 공유 주소만 등록할 수 있습니다. (예: https://arcade.makecode.com/S65139-04309-19865-34038)',
            simulatorFieldLabel: '',
            simulatorPlaceholder: '',
            simulatorHelp: '',
            cautionLabel: '',
            cautionPlaceholder: '',
            extensionLabel: '',
            extensionPlaceholder: ''
          }
        : {
          pageTitle: '에셋 팩',
          pageLead:
            'MakeCode Arcade에서 프로젝트를 공유하면 받은 주소(예: https://arcade.makecode.com/S65139-04309-19865-34038)를 올리고, 스크린샷·소스(zip 등) 첨부를 함께 넣을 수 있습니다.',
          registerCta: '에셋 팩 등록',
          listLoadError: '에셋 팩 목록을 불러오지 못했습니다.',
          empty: '아직 등록된 에셋 팩이 없습니다.',
          dialogTitle: '에셋 팩 등록',
          deleteTitle: '에셋 팩 삭제',
          successRegister: '에셋 팩이 등록되었습니다.',
          descPlaceholder: '어떤 스프라이트·타일이 들어 있는지 간단히 적어 주세요.',
          titlePlaceholder: '예: 캐릭터 세트',
          shareUrlFieldLabel: 'MakeCode 공유 주소',
          shareUrlPlaceholder: 'https://arcade.makecode.com/S65139-04309-19865-34038',
          shareUrlHelp: 'Arcade 편집기에서 「공유」로 받은 링크만 붙여 넣을 수 있습니다.',
          shareUrlError:
            'MakeCode Arcade 공유 주소만 등록할 수 있습니다. (예: https://arcade.makecode.com/S65139-04309-19865-34038)',
          simulatorFieldLabel: '',
          simulatorPlaceholder: '',
          simulatorHelp: '',
          cautionLabel: '',
          cautionPlaceholder: '',
          extensionLabel: '',
          extensionPlaceholder: ''
        };
  const [packs, setPacks] = useState<ArcadeAssetPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [shareUrlInput, setShareUrlInput] = useState('');
  const [simulatorCodeInput, setSimulatorCodeInput] = useState('');
  const [cautionNoteInput, setCautionNoteInput] = useState('');
  const [requiredExtensionsInput, setRequiredExtensionsInput] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packToDelete, setPackToDelete] = useState<string | null>(null);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [howToGuideOpen, setHowToGuideOpen] = useState(false);
  /** 코드 예제만: 본인 글 또는 superAdmin 수정 */
  const [editingPack, setEditingPack] = useState<ArcadeAssetPack | null>(null);

  const { currentUser, loading: authLoading } = useAuth();
  const { isMobile } = useLayoutContext();
  const isSuperAdmin = currentUser?.role === 'superAdmin';

  const fetchPacks = useCallback(async () => {
    try {
      const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      let list = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as ArcadeAssetPack[];
      if (isCodeExample) {
        list = list.filter(p => isCodeExampleCategory(p.category));
      } else if (isMusic) {
        list = list.filter(p => isMusicCategory(p.category));
      } else {
        list = list.filter(
          p => !isCodeExampleCategory(p.category) && !isMusicCategory(p.category)
        );
      }
      setPacks(list);
    } catch (e) {
      console.error('에셋 팩 목록 조회 오류:', e);
      toast.error(labels.listLoadError);
    } finally {
      setLoading(false);
    }
  }, [isCodeExample, isMusic]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const resetForm = () => {
    setEditingPack(null);
    setTitle('');
    setDescription('');
    setShareUrlInput('');
    setSimulatorCodeInput('');
    setCautionNoteInput('');
    setRequiredExtensionsInput('');
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setAttachmentFile(null);
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setScreenshotFile(null);
      setScreenshotPreview(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 선택할 수 있습니다.');
      e.target.value = '';
      return;
    }
    if (file.size > SCREENSHOT_MAX_BYTES) {
      toast.error('스크린샷은 5MB 이하로 올려 주세요.');
      e.target.value = '';
      return;
    }
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAttachmentFile(null);
      return;
    }
    const lower = file.name.toLowerCase();
    const okExt =
      lower.endsWith('.zip') ||
      lower.endsWith('.bundle') ||
      lower.endsWith('.gz') ||
      lower.endsWith('.tar.gz');
    if (!okExt) {
      toast.error('zip, tar.gz, git bundle(.bundle) 파일만 올릴 수 있습니다.');
      e.target.value = '';
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error('첨부 파일은 25MB 이하로 올려 주세요.');
      e.target.value = '';
      return;
    }
    setAttachmentFile(file);
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      toast.error('로그인 후 등록할 수 있습니다.');
      return;
    }
    const shareUrl = isCodeExample
      ? parseCodeExampleShareUrl(shareUrlInput)
      : parseArcadeShareUrlStrict(shareUrlInput);
    if (!title.trim()) {
      toast.error('제목을 입력해 주세요.');
      return;
    }
    if (!shareUrl) {
      toast.error(labels.shareUrlError);
      return;
    }
    if (isCodeExample) {
      const sim = simulatorCodeInput.trim();
      if (!sim) {
        toast.error('시뮬레이터 임베드 코드를 입력해 주세요.');
        return;
      }
      if (sim.length > SIMULATOR_CODE_MAX_CHARS) {
        toast.error(`시뮬레이터 코드는 ${SIMULATOR_CODE_MAX_CHARS.toLocaleString()}자 이하로 입력해 주세요.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      if (editingPack) {
        const patch: UpdateData<ArcadeAssetPack> = {
          title: title.trim(),
          description: description.trim(),
          shareUrl
        };
        if (isCodeExample) {
          patch.simulatorCode = simulatorCodeInput.trim();
          patch.cautionNote = cautionNoteInput.trim() ? cautionNoteInput.trim() : deleteField();
          patch.requiredExtensions = requiredExtensionsInput.trim()
            ? requiredExtensionsInput.trim()
            : deleteField();
        }
        if (isGalleryLike) {
          if (screenshotFile) {
            const ext = screenshotFile.name.split('.').pop()?.toLowerCase() || 'jpg';
            const safeExt = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext) ? ext : 'jpg';
            const path = `${storagePrefix}/${currentUser.uid}/${Date.now()}.${safeExt}`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, screenshotFile);
            patch.previewImageUrl = await getDownloadURL(storageRef);
          }
          if (attachmentFile) {
            const safe = sanitizeFileName(attachmentFile.name);
            const path = `${storagePrefix}/${currentUser.uid}/${Date.now()}_${safe}`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, attachmentFile);
            patch.attachmentUrl = await getDownloadURL(storageRef);
            patch.attachmentFileName = attachmentFile.name;
          }
        }
        await updateDoc(doc(db, COLLECTION, editingPack.id), patch);
        toast.success(
          isCodeExample
            ? '코드 예제가 수정되었습니다.'
            : isMusic
              ? '음악 프로젝트가 수정되었습니다.'
              : isGallery
                ? '에셋 팩이 수정되었습니다.'
                : '수정되었습니다.'
        );
        setDialogOpen(false);
        resetForm();
        await fetchPacks();
        return;
      }

      let previewImageUrl: string | undefined;
      if (screenshotFile && isGalleryLike) {
        const ext = screenshotFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const safeExt = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext) ? ext : 'jpg';
        const path = `${storagePrefix}/${currentUser.uid}/${Date.now()}.${safeExt}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, screenshotFile);
        previewImageUrl = await getDownloadURL(storageRef);
      }

      let attachmentUrl: string | undefined;
      let attachmentFileName: string | undefined;
      if (attachmentFile && isGalleryLike) {
        const safe = sanitizeFileName(attachmentFile.name);
        const path = `${storagePrefix}/${currentUser.uid}/${Date.now()}_${safe}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, attachmentFile);
        attachmentUrl = await getDownloadURL(storageRef);
        attachmentFileName = attachmentFile.name;
      }

      const category: ArcadePackListing = isCodeExample
        ? 'codeExample'
        : isMusic
          ? 'music'
          : 'gallery';

      await addDoc(collection(db, COLLECTION), {
        title: title.trim(),
        description: description.trim(),
        shareUrl,
        authorId: currentUser.uid,
        authorName: currentUser.name?.trim() || currentUser.email || '사용자',
        createdAt: Timestamp.now(),
        category,
        ...(isCodeExample ? { simulatorCode: simulatorCodeInput.trim() } : {}),
        ...(isCodeExample && cautionNoteInput.trim() ? { cautionNote: cautionNoteInput.trim() } : {}),
        ...(isCodeExample && requiredExtensionsInput.trim()
          ? { requiredExtensions: requiredExtensionsInput.trim() }
          : {}),
        ...(previewImageUrl ? { previewImageUrl } : {}),
        ...(attachmentUrl ? { attachmentUrl, attachmentFileName } : {})
      });
      toast.success(labels.successRegister);
      setDialogOpen(false);
      resetForm();
      await fetchPacks();
    } catch (e) {
      console.error('에셋 팩 등록 오류:', e);
      const detail = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : '';
      toast.error(
        detail
          ? `등록에 실패했습니다. ${detail}`
          : '등록에 실패했습니다. 로그인 상태와 Firestore 규칙 배포를 확인해 주세요.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('공유 주소가 클립보드에 복사되었습니다.');
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!packToDelete) return;
    try {
      await deleteDoc(doc(db, COLLECTION, packToDelete));
      toast.success('삭제되었습니다.');
      setDeleteDialogOpen(false);
      setPackToDelete(null);
      fetchPacks();
    } catch (e) {
      console.error('에셋 팩 삭제 오류:', e);
      toast.error('삭제에 실패했습니다.');
    }
  };

  const packToDeleteData = packs.find(p => p.id === packToDelete);
  const isAuthor = packToDeleteData?.authorId === currentUser?.uid;

  const pageShellStyle: React.CSSProperties = usesMainLayout
    ? {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? 0 : '20px',
        width: '100%',
        boxSizing: 'border-box',
      }
    : getMakecodeArcadePageRootStyle(isMobile);

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
    gap: '24px',
    alignItems: 'start',
    width: '100%',
  };

  const showGridSkeleton = loading && packs.length === 0;
  const skeletonCardCount = isMobile ? 4 : 6;

  const inner = (
    <div style={pageShellStyle}>
      <style>{`@keyframes assetPackSkeletonShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      {usesMainLayout && (
        <div style={{ marginBottom: '16px' }}>
          <Button
            variant="line"
            leftIcon={<LeftArrowIcon width={20} height={20} />}
            onClick={() => navigate('/makecodearcade/extensions')}
          >
            확장 기능 목록
          </Button>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}
      >
        <div>
          <h1
            style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontFamily: 'var(--font-family-primary)',
              color: 'var(--Color-text-strong)'
            }}
          >
            {labels.pageTitle}
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              color: 'var(--Color-text-normal)',
              lineHeight: 1.5,
              maxWidth: '640px'
            }}
          >
            {labels.pageLead}
            {isGalleryLike && (
              <>
                {' '}
                (예:{' '}
                <span style={{ wordBreak: 'break-all' }}>https://arcade.makecode.com/S65139-04309-19865-34038</span>
                )
              </>
            )}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            minHeight: 40,
            flexShrink: 0,
          }}
        >
          {authLoading ? (
            <>
              {isGallery && (
                <div style={{ height: 40, width: 168, borderRadius: 8, ...skeletonPulseStyle }} />
              )}
              <div style={{ height: 40, width: 128, borderRadius: 8, ...skeletonPulseStyle }} />
            </>
          ) : (
            <>
              {isGallery && (
                <Button variant="outline" onClick={() => setHowToGuideOpen(true)}>
                  에셋 팩 만드는 방법
                </Button>
              )}
              {currentUser && (
                <Button
                  variant="primary"
                  leftIcon={<PlusIcon width={20} height={20} fill="#FFFFFF" />}
                  onClick={() => {
                    resetForm();
                    setDialogOpen(true);
                  }}
                >
                  {labels.registerCta}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {!authLoading && !currentUser && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: 'var(--Color-background-main)',
            fontSize: '14px',
            color: 'var(--Color-text-normal)'
          }}
        >
          목록은 누구나 볼 수 있고, 등록은 로그인한 회원만 가능합니다.
        </div>
      )}

      <div style={gridStyle}>
        {showGridSkeleton
          ? Array.from({ length: skeletonCardCount }).map((_, i) => <PackCardSkeleton key={i} />)
          : packs.map(pack => (
          <Card
            key={pack.id}
            padding="16px"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              ...(isCodeExample
                ? { height: 'auto', alignSelf: 'start', minWidth: 0 }
                : { height: 'auto', alignSelf: 'start', minWidth: 0 })
            }}
          >
            {isGalleryLike ? (
              <div
                style={{
                  width: '100%',
                  aspectRatio: PACK_CARD_PREVIEW_ASPECT,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'var(--Color-background-main)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {pack.previewImageUrl ? (
                  <img
                    src={pack.previewImageUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 13, color: '#989898' }}>미리보기 없음</span>
                )}
              </div>
            ) : pack.previewImageUrl ? (
              <div
                style={{
                  width: '100%',
                  aspectRatio: PACK_CARD_PREVIEW_ASPECT,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'var(--Color-background-main)',
                }}
              >
                <img
                  src={pack.previewImageUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </div>
            ) : null}
            <div
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--Color-text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {pack.title}
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              {pack.authorName} · {pack.createdAt?.toDate?.().toLocaleDateString?.() ?? ''}
            </div>
            {isCodeExample && pack.simulatorCode ? (
              <div style={{ width: '100%', minWidth: 0, flexShrink: 0 }}>
                <CodeExampleSimulatorCardPreview html={pack.simulatorCode} />
              </div>
            ) : null}
            {pack.description ? (
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--Color-text-normal)',
                  lineHeight: 1.5,
                  ...(isCodeExample
                    ? { flex: 'none', maxHeight: 160, overflow: 'auto' }
                    : {})
                }}
              >
                {pack.description}
              </div>
            ) : null}
            {isCodeExample && pack.requiredExtensions ? (
              <div style={{ fontSize: '12px', color: 'var(--Color-text-normal)' }}>
                필요한 확장: {pack.requiredExtensions}
              </div>
            ) : null}
            {isCodeExample && pack.cautionNote ? (
              <div
                style={{
                  fontSize: '12px',
                  color: '#8A6D3B',
                  background: '#FFF8E1',
                  border: '1px solid #FFE0B2',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  lineHeight: 1.5
                }}
              >
                유의사항: {pack.cautionNote}
              </div>
            ) : null}
            {isCodeExample ? (
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>공유 링크</div>
            ) : null}
            <div
              style={{
                fontSize: '12px',
                padding: '8px 10px',
                borderRadius: '8px',
                background: 'var(--Color-background-main)',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                color: 'var(--Color-text-normal)'
              }}
            >
              {pack.shareUrl}
            </div>
            {pack.attachmentFileName ? (
              <div style={{ fontSize: '11px', color: 'var(--Color-text-normal)' }}>
                첨부: {pack.attachmentFileName}
              </div>
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: 'auto' }}>
              <Button
                variant="outline"
                size="small"
                onClick={() => window.open(pack.shareUrl, '_blank', 'noopener,noreferrer')}
              >
                MakeCode에서 열기
              </Button>
              {pack.attachmentUrl ? (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => window.open(pack.attachmentUrl!, '_blank', 'noopener,noreferrer')}
                >
                  첨부 다운로드
                </Button>
              ) : null}
              <button
                type="button"
                title="주소 복사"
                onClick={() => handleCopyUrl(pack.shareUrl)}
                style={{
                  padding: '8px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer'
                }}
              >
                <ContentCopyIcon width={20} height={20} />
              </button>
              {currentUser && (pack.authorId === currentUser.uid || isSuperAdmin) ? (
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    alignItems: 'center'
                  }}
                >
                  {isCodeExample || isGalleryLike ? (
                    <button
                      type="button"
                      title="수정"
                      onClick={() => {
                        setEditingPack(pack);
                        setTitle(pack.title);
                        setDescription(pack.description ?? '');
                        setShareUrlInput(pack.shareUrl ?? '');
                        setSimulatorCodeInput(pack.simulatorCode ?? '');
                        setCautionNoteInput(pack.cautionNote ?? '');
                        setRequiredExtensionsInput(pack.requiredExtensions ?? '');
                        setScreenshotFile(null);
                        setScreenshotPreview(isGalleryLike ? (pack.previewImageUrl ?? null) : null);
                        setAttachmentFile(null);
                        setDialogOpen(true);
                      }}
                      style={{
                        padding: '8px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <EditPencilIcon width={20} height={20} color="var(--Color-text-normal, #666)" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    title="삭제"
                    onClick={() => {
                      setPackToDelete(pack.id);
                      setDeleteDialogOpen(true);
                    }}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer'
                    }}
                  >
                    <TrashIcon width={20} height={20} color="#F44336" />
                  </button>
                </div>
              ) : null}
            </div>
          </Card>
        ))}
      </div>

      {packs.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--Color-text-normal)' }}>
          {labels.empty}
        </div>
      )}

      {howToGuideOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="asset-pack-howto-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '16px',
            boxSizing: 'border-box'
          }}
          onClick={() => setHowToGuideOpen(false)}
        >
          <div
            style={{
              background: 'var(--Color-background-normal)',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '720px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxSizing: 'border-box'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '16px',
                flexWrap: 'wrap'
              }}
            >
              <h2
                id="asset-pack-howto-title"
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontFamily: 'var(--font-family-primary)',
                  color: 'var(--Color-text-strong)'
                }}
              >
                에셋 팩 만드는 방법
              </h2>
              <Button variant="cancel" onClick={() => setHowToGuideOpen(false)}>
                닫기
              </Button>
            </div>
            <AssetPackHowToGuideContent />
          </div>
        </div>
      )}

      {dialogOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
          }}
          onClick={() => {
            if (!submitting) {
              setDialogOpen(false);
              resetForm();
            }
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>
              {editingPack
                ? isCodeExample
                  ? '코드 예제 수정'
                  : isMusic
                    ? '음악 프로젝트 수정'
                    : '에셋 팩 수정'
                : labels.dialogTitle}
            </h3>
            {editingPack && isSuperAdmin && editingPack.authorId !== currentUser?.uid ? (
              <p
                style={{
                  margin: '0 0 16px',
                  fontSize: '13px',
                  color: '#F57C00',
                  lineHeight: 1.5
                }}
              >
                슈퍼관리자 권한으로 다른 사용자가 등록한 항목을 수정합니다.
              </p>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <InputField
                label="제목"
                value={title}
                onChange={setTitle}
                placeholder={labels.titlePlaceholder}
                required
                width="100%"
              />
              <TextAreaField
                label="설명 (선택)"
                value={description}
                onChange={setDescription}
                rows={3}
                placeholder={labels.descPlaceholder}
                width="100%"
              />
              <InputField
                label={labels.shareUrlFieldLabel}
                value={shareUrlInput}
                onChange={setShareUrlInput}
                placeholder={labels.shareUrlPlaceholder}
                required
                width="100%"
              />
              <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
                {labels.shareUrlHelp}
              </p>
              {isCodeExample && (
                <>
                  <TextAreaField
                    label={labels.simulatorFieldLabel}
                    value={simulatorCodeInput}
                    onChange={setSimulatorCodeInput}
                    rows={4}
                    placeholder={labels.simulatorPlaceholder}
                    required
                    width="100%"
                  />
                  <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
                    {labels.simulatorHelp}
                  </p>
                  <InputField
                    label={labels.extensionLabel}
                    value={requiredExtensionsInput}
                    onChange={setRequiredExtensionsInput}
                    placeholder={labels.extensionPlaceholder}
                    width="100%"
                  />
                  <TextAreaField
                    label={labels.cautionLabel}
                    value={cautionNoteInput}
                    onChange={setCautionNoteInput}
                    rows={2}
                    placeholder={labels.cautionPlaceholder}
                    width="100%"
                  />
                </>
              )}
              {isGalleryLike && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                      스크린샷 (선택)
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleScreenshotChange}
                      disabled={submitting}
                      style={{ fontSize: '14px' }}
                    />
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      에디터나 미리보기 화면을 캡처해 올리면 목록에서 구분하기 쉽습니다. (최대 5MB)
                    </p>
                    {screenshotPreview ? (
                      <img
                        src={screenshotPreview}
                        alt="미리보기"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '160px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid var(--Color-line-normal)'
                        }}
                      />
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                      소스·백업 첨부 (선택)
                    </span>
                    <input
                      type="file"
                      accept=".zip,.bundle,.gz,.tar.gz,application/zip,application/x-gzip"
                      onChange={handleAttachmentChange}
                      disabled={submitting}
                      style={{ fontSize: '14px' }}
                    />
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      프로젝트 zip, git bundle(.bundle), tar.gz 등 (최대 25MB)
                    </p>
                    {attachmentFile ? (
                      <span style={{ fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                        선택됨: {attachmentFile.name}
                      </span>
                    ) : null}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button
                  variant="cancel"
                  onClick={() => {
                    if (!submitting) {
                      setDialogOpen(false);
                      resetForm();
                    }
                  }}
                  disabled={submitting}
                >
                  취소
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting
                    ? editingPack
                      ? '저장 중…'
                      : '등록 중…'
                    : editingPack
                      ? '저장'
                      : '등록'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteDialogOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
          }}
          onClick={() => {
            setDeleteDialogOpen(false);
            setPackToDelete(null);
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>{labels.deleteTitle}</h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px' }}>
              이 항목을 삭제할까요? 되돌릴 수 없습니다.
            </p>
            {isSuperAdmin && !isAuthor && (
              <p
                style={{
                  margin: '0 0 16px',
                  fontSize: '13px',
                  color: '#F57C00',
                  fontStyle: 'italic'
                }}
              >
                슈퍼관리자 권한으로 다른 사용자가 등록한 항목을 삭제합니다.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                variant="cancel"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setPackToDelete(null);
                }}
              >
                취소
              </Button>
              <Button variant="primary" onClick={handleDelete} style={{ background: '#F44336' }}>
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (usesMainLayout) {
    return <MainLayout>{inner}</MainLayout>;
  }
  return inner;
};

export default AssetPackShare;
